import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Profile, RolNivel } from '@/types'

// Queries that power both the Crew tab (manage the whole roster) and the
// TourSetup crew picker (quick assign from team while creating a tour).
// Extracted from CrewView so TourSetup can import them without tripping
// react-refresh (which forbids non-component exports from component files).

export type MemberRow = {
  user_id: string
  rol_nivel: RolNivel
  profile: Profile | null
}

// Per-user assignment counts inside a tour. Powers the remove-confirmation
// warning: "also unassigns from N activities". Computed in JS after two
// round-trips because PostgREST doesn't expose group-by aggregation.
export function useTourAssignmentCounts(tourId: string | null | undefined) {
  return useQuery({
    queryKey: ['tour-assignment-counts', tourId],
    enabled: !!tourId,
    queryFn: async (): Promise<Map<string, number>> => {
      const { data: activities, error: aErr } = await supabase
        .from('activities')
        .select('id')
        .eq('tour_id', tourId!)
      if (aErr) throw aErr
      const activityIds = (activities ?? []).map(a => a.id as string)
      if (activityIds.length === 0) return new Map()

      const { data: assignments, error: asErr } = await supabase
        .from('activity_assignments')
        .select('user_id')
        .in('activity_id', activityIds)
      if (asErr) throw asErr

      const counts = new Map<string, number>()
      for (const a of (assignments ?? [])) {
        counts.set(a.user_id as string, (counts.get(a.user_id as string) ?? 0) + 1)
      }
      return counts
    },
  })
}

// Fetches tour_memberships joined with profiles. Returns the same shape as
// the previous org-members query so the UI doesn't care where the roster
// came from.
export function useTourMembers(tourId: string | null | undefined) {
  return useQuery({
    queryKey: ['tour-members', tourId],
    enabled: !!tourId,
    queryFn: async (): Promise<MemberRow[]> => {
      const { data: rows, error } = await supabase
        .from('tour_memberships')
        .select('user_id, rol_nivel')
        .eq('tour_id', tourId!)
        .order('rol_nivel', { ascending: true })
      if (error) throw error
      const members = (rows ?? []) as { user_id: string; rol_nivel: RolNivel }[]
      if (members.length === 0) return []

      const { data: profiles, error: pErr } = await supabase
        .from('profiles')
        .select('*')
        .in('id', members.map(m => m.user_id))
      if (pErr) throw pErr

      const byId = new Map<string, Profile>((profiles ?? []).map(p => [p.id, p as Profile]))
      return members.map(m => ({
        user_id: m.user_id,
        rol_nivel: m.rol_nivel,
        profile: byId.get(m.user_id) ?? null,
      }))
    },
  })
}

// Org members not already assigned to the tour — powers the "Assign from
// team" picker. Only managers run this (RLS allows them to see all org
// memberships).
export function useAssignableOrgMembers(orgId: string | null | undefined, tourMemberIds: Set<string>) {
  return useQuery({
    queryKey: ['org-members-for-assign', orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<MemberRow[]> => {
      const { data: rows, error } = await supabase
        .from('organization_memberships')
        .select('user_id, rol_nivel')
        .eq('org_id', orgId!)
      if (error) throw error
      const members = (rows ?? []) as { user_id: string; rol_nivel: RolNivel }[]
      if (members.length === 0) return []

      const { data: profiles, error: pErr } = await supabase
        .from('profiles')
        .select('*')
        .in('id', members.map(m => m.user_id))
      if (pErr) throw pErr

      const byId = new Map<string, Profile>((profiles ?? []).map(p => [p.id, p as Profile]))
      return members.map(m => ({
        user_id: m.user_id,
        rol_nivel: m.rol_nivel,
        profile: byId.get(m.user_id) ?? null,
      }))
    },
    select: (all) => all.filter(m => !tourMemberIds.has(m.user_id)),
  })
}
